'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { InformationCircleIcon } from '@terra/ui-icons';
import { useState, useCallback, useEffect } from 'react';
import { Switch } from '../../components/switch';
import { Alert } from '../../components/alert';
import { BodyStrong, MetaDefault } from '../../components/typography';
import { useTranslations } from 'next-intl';
import { MfaProviderSchema } from '@terra/shared-schemas-orchestrator';
import type { MfaMessageDto } from '@terra/shared-contracts-applications';
import type {
  MfaCredentialBinding,
  MfaFetchMessagesFn,
  MfaFetchWebhookConfigFn,
  MfaProvider,
  MfaProviderOption,
  MfaTotpSecretRevealProps,
} from './mfa.types';
import { MfaStatusDisplay } from './MfaStatusDisplay';
import { MfaOtpTotpSecretField } from './MfaOtpTotpSecretField';
import { useLiveOtpCode } from './use-live-otp-code';

export type MfaProviderRowPanelProps = MfaCredentialBinding & {
  providerOption: MfaProviderOption;
  mfaProvider: MfaProvider | null;
  onToggle: (enabled: boolean) => void;
  onTotpSecretChange?: (secret: string) => void;
  onFetchMfaMessages?: MfaFetchMessagesFn;
  onFetchMfaWebhookConfig?: MfaFetchWebhookConfigFn;
  totpSecretReveal?: MfaTotpSecretRevealProps;
};
export function MfaProviderRowPanel({
  providerOption,
  mfaProvider,
  credentialId,
  applicationId,
  onToggle,
  onTotpSecretChange,
  onFetchMfaMessages,
  onFetchMfaWebhookConfig,
  totpSecretReveal,
}: MfaProviderRowPanelProps) {
  const t = useTranslations('designSystem.mfa');
  const Icon = providerOption.icon;
  const isEnabled = mfaProvider?.enabled ?? false;
  const isExisting = Boolean(mfaProvider?.id);
  const mfaConfigId = mfaProvider?.id;
  const [messages, setMessages] = useState<MfaMessageDto[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const isOtpProviderRow = providerOption.value === MfaProviderSchema.enum.OTP;
  const { otpCode, isOtpLoading, fetchOtpCode } = useLiveOtpCode({
    credentialId: credentialId ?? '',
    mfaConfigId: mfaConfigId ?? '',
    fetchMfaMessages: onFetchMfaMessages,
    enabled:
      Boolean(credentialId) &&
      Boolean(mfaConfigId) &&
      isExisting &&
      isEnabled &&
      isOtpProviderRow,
  });
  const fetchMessages = useCallback(async () => {
    if (
      !credentialId ||
      !onFetchMfaMessages ||
      !mfaConfigId ||
      !isExisting ||
      providerOption.value === MfaProviderSchema.enum.OTP
    ) {
      return;
    }

    setIsLoadingMessages(true);

    try {
      const result = await onFetchMfaMessages({
        credentialId,
        mfaConfigId,
      });

      if (result.success && result.messages) {
        setMessages(result.messages);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [
    credentialId,
    onFetchMfaMessages,
    mfaConfigId,
    providerOption.value,
    isExisting,
  ]);

  useEffect(() => {
    if (!isExisting || !isEnabled) return;
    if (providerOption.value === MfaProviderSchema.enum.OTP) {
      return;
    }

    void fetchMessages();
  }, [isExisting, isEnabled, providerOption.value, fetchMessages]);
  const storedTotpSecret = mfaProvider?.totpSecret ?? '';
  const trimmedTotpSecret = storedTotpSecret.trim();
  const showOtpSecretForExistingConfig =
    Boolean(totpSecretReveal) &&
    (trimmedTotpSecret.length > 0 ||
      Boolean(totpSecretReveal?.isDefaultSecretValue));
  const otpTotpSecretEditor = (
    <MfaOtpTotpSecretField
      visible={
        isOtpProviderRow && (!isExisting || showOtpSecretForExistingConfig)
      }
      storedTotpSecret={storedTotpSecret}
      onTotpSecretChange={onTotpSecretChange}
      totpSecretReveal={totpSecretReveal}
    />
  );

  return (
    <Box
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.muted"
      bg="bg.default"
    >
      <HStack p={3} gap={3} alignItems="center">
        <Icon size={18} />
        <VStack alignItems="stretch" gap={0} flex={1}>
          <BodyStrong color="fg.default">{providerOption.label}</BodyStrong>
          <MetaDefault color="fg.muted">
            {providerOption.description}
          </MetaDefault>
        </VStack>
        <Switch
          size="sm"
          checked={isEnabled}
          onCheckedChange={(details) => onToggle(details.checked)}
        />
      </HStack>

      {isEnabled &&
      !(isExisting && providerOption.value === MfaProviderSchema.enum.EMAIL) ? (
        <Box px={3} pb={3}>
          {isExisting && mfaProvider ? (
            <VStack alignItems="stretch" gap={3}>
              <MfaStatusDisplay
                mfaProvider={mfaProvider}
                otpCode={otpCode}
                isOtpLoading={isOtpLoading}
                onRefreshOtp={fetchOtpCode}
                messages={messages}
                isLoadingMessages={isLoadingMessages}
                onFetchMessages={fetchMessages}
                applicationId={applicationId}
                credentialId={credentialId}
                onFetchWebhookConfig={onFetchMfaWebhookConfig}
              />
              {otpTotpSecretEditor}
            </VStack>
          ) : (
            <VStack alignItems="stretch" gap={3}>
              {otpTotpSecretEditor}
              <Alert.Root>
                <Alert.Icon asChild>
                  <InformationCircleIcon />
                </Alert.Icon>
                <Alert.Content>
                  <Alert.Description>{t('setupMessage')}</Alert.Description>
                </Alert.Content>
              </Alert.Root>
            </VStack>
          )}
        </Box>
      ) : null}
    </Box>
  );
}
